import { useState, useEffect } from "react";
import { Plus, Clock, AlertTriangle, Check, CheckCircle, Calendar, FileText, Users, Building2, CalendarClock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Todo, Customer } from "@/types/database";
import { Link } from "react-router-dom";
import { useCustomerManagement } from "@/hooks/useCustomerManagement";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface DefaultTask {
  id: string;
  name: string;
  description: string | null;
  due_in_days: number;
  source: 'organization' | 'team';
}

export const HomeTodos = () => {
  const { user } = useAuth();
  const { addProtocolEntry } = useCustomerManagement();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [isCompletingTodo, setIsCompletingTodo] = useState(false);
  const [completionComment, setCompletionComment] = useState("");
  
  // New state for task selection flow
  const [defaultTasks, setDefaultTasks] = useState<DefaultTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [selectedTask, setSelectedTask] = useState<DefaultTask | null>(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [userProfile, setUserProfile] = useState<{ organization_id: string | null; team_id: string | null } | null>(null);
  
  const [newTodo, setNewTodo] = useState<{
    customerId: string;
    customerName: string;
    customTitle: string;
    customDescription: string;
    customDays: number;
  }>({
    customerId: "",
    customerName: "",
    customTitle: "",
    customDescription: "",
    customDays: 7,
  });

  // Fetch user profile for team/org
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('organization_id, team_id')
        .eq('user_id', user.id)
        .single();
      if (data) setUserProfile(data);
    };
    fetchProfile();
  }, [user]);

  // Fetch default tasks when dialog opens
  useEffect(() => {
    if (isCreating && userProfile) {
      fetchDefaultTasks();
    }
  }, [isCreating, userProfile]);

  const fetchDefaultTasks = async () => {
    if (!userProfile) return;

    setIsLoadingTasks(true);
    try {
      const allTasks: DefaultTask[] = [];

      // Fetch organization tasks
      if (userProfile.organization_id) {
        const { data: orgTasks } = await supabase
          .from('default_organization_tasks')
          .select('id, name, description, due_in_days')
          .eq('organization_id', userProfile.organization_id)
          .order('due_in_days', { ascending: true });

        // Get team settings for org tasks
        let enabledOrgTaskIds: Set<string> | null = null;
        if (userProfile.team_id && orgTasks) {
          const { data: settings } = await supabase
            .from('team_organization_task_settings')
            .select('organization_task_id, is_enabled')
            .eq('team_id', userProfile.team_id);

          if (settings && settings.length > 0) {
            const settingsMap = new Map(settings.map(s => [s.organization_task_id, s.is_enabled]));
            enabledOrgTaskIds = new Set(
              orgTasks
                .filter(task => !settingsMap.has(task.id) || settingsMap.get(task.id) === true)
                .map(task => task.id)
            );
          }
        }

        if (orgTasks) {
          const filteredOrgTasks = enabledOrgTaskIds
            ? orgTasks.filter(task => enabledOrgTaskIds!.has(task.id))
            : orgTasks;

          allTasks.push(...filteredOrgTasks.map(task => ({
            id: task.id,
            name: task.name,
            description: task.description,
            due_in_days: task.due_in_days,
            source: 'organization' as const,
          })));
        }
      }

      // Fetch team tasks
      if (userProfile.team_id) {
        const { data: teamTasks } = await supabase
          .from('default_team_tasks')
          .select('id, name, description, due_in_days')
          .eq('team_id', userProfile.team_id)
          .order('due_in_days', { ascending: true });

        if (teamTasks) {
          allTasks.push(...teamTasks.map(task => ({
            id: task.id,
            name: task.name,
            description: task.description,
            due_in_days: task.due_in_days || 7,
            source: 'team' as const,
          })));
        }
      }

      // Sort: team first, then by due_in_days
      allTasks.sort((a, b) => {
        if (a.source !== b.source) return a.source === 'team' ? -1 : 1;
        return a.due_in_days - b.due_in_days;
      });

      setDefaultTasks(allTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoadingTasks(false);
    }
  };

  useEffect(() => {
    const setupRealtimeAndLoadData = async () => {
      const loadTodos = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            setLoading(false);
            return;
          }

          const { data, error } = await supabase
            .from('todos')
            .select('*')
            .eq('completed', false)
            .order('due_date', { ascending: true })
            .limit(4);

          if (error) {
            console.error('Error loading todos:', error);
          } else {
            setTodos(data || []);
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setLoading(false);
        }
      };

      await loadTodos();
      loadCustomers();

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const channel = supabase
          .channel('hometodos-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'todos',
              filter: `user_id=eq.${session.user.id}`
            },
            () => loadTodos()
          )
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      }
    };

    setupRealtimeAndLoadData();
  }, []);

  const loadTodos = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('todos')
        .select('*')
        .eq('completed', false)
        .order('due_date', { ascending: true })
        .limit(4);

      if (error) {
        console.error('Error loading todos:', error);
      } else {
        setTodos(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('customers')
        .select('id, first_name, last_name')
        .order('first_name', { ascending: true });

      if (error) {
        console.error('Error loading customers:', error);
      } else {
        const customersWithDisplayName = (data || []).map(customer => ({
          id: customer.id,
          first_name: customer.first_name,
          last_name: customer.last_name,
          name: `${customer.first_name} ${customer.last_name}`.trim(),
          user_id: '',
          created_at: '',
          updated_at: '',
          email: null,
          phone: null,
          address: null,
          notes: null
        }));
        setCustomers(customersWithDisplayName);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const calculateDueDate = (reminderDays: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + reminderDays);
    return date.toISOString().split('T')[0];
  };

  const handleTaskSelect = (task: DefaultTask) => {
    setSelectedTask(task);
    setNewTodo(prev => ({
      ...prev,
      customTitle: task.name,
      customDescription: task.description || '',
      customDays: task.due_in_days
    }));
  };

  const createTodo = async () => {
    if (!newTodo.customerId || !newTodo.customerName) {
      toast.error("Bitte wählen Sie einen Kunden aus.");
      return;
    }

    if (!selectedTask && !showCustomForm) {
      toast.error("Bitte wählen Sie eine Aufgabe aus.");
      return;
    }

    if (showCustomForm && (!newTodo.customTitle || !newTodo.customDescription)) {
      toast.error("Bitte füllen Sie Titel und Beschreibung aus.");
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast.error("Sie müssen angemeldet sein.");
      return;
    }

    try {
      // Retry logic for mobile reliability
      let data = null;
      let lastError = null;
      const maxAttempts = 2;
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const result = await supabase
            .from('todos')
            .insert({
              customer_id: newTodo.customerId,
              title: newTodo.customTitle,
              description: newTodo.customDescription,
              due_date: calculateDueDate(newTodo.customDays),
              priority: 'medium',
              user_id: session.user.id
            })
            .select()
            .single();

          if (result.error) throw result.error;
          data = result.data;
          break;
        } catch (e) {
          console.warn(`Todo insert attempt ${attempt} failed:`, e);
          lastError = e;
          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }

      if (!data) {
        console.error('Error creating todo after retries:', lastError);
        toast.error("To-Do konnte nicht erstellt werden. Bitte erneut versuchen.");
        return;
      }

      resetDialog();
      loadTodos();

      // Send email notification (non-blocking)
      supabase.functions.invoke('send-task-notification', {
        body: { todoId: data.id, type: 'created' }
      }).catch(emailError => {
        console.error('Error sending task notification:', emailError);
      });

      toast.success(`${data.title} wurde zur Liste hinzugefügt.`);
    } catch (error) {
      console.error('Error:', error);
      toast.error("Ein unerwarteter Fehler ist aufgetreten. Bitte erneut versuchen.");
    }
  };

  const resetDialog = () => {
    setIsCreating(false);
    setSelectedTask(null);
    setShowCustomForm(false);
    setNewTodo({
      customerId: "",
      customerName: "",
      customTitle: "",
      customDescription: "",
      customDays: 7,
    });
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPriorityColor = (daysUntil: number) => {
    if (daysUntil < 0) return "destructive";
    if (daysUntil <= 3) return "destructive";
    if (daysUntil <= 7) return "outline";
    return "secondary";
  };

  const getPriorityIcon = (daysUntil: number) => {
    if (daysUntil < 0) return AlertTriangle;
    if (daysUntil <= 3) return AlertTriangle;
    return Clock;
  };

  const getDueDaysLabel = (days: number) => {
    if (days === 1) return '1 Tag';
    if (days === 7) return '1 Woche';
    if (days === 14) return '2 Wochen';
    if (days === 30) return '1 Monat';
    return `${days} Tage`;
  };

  const getDueDaysColor = (days: number) => {
    if (days === 1) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    if (days <= 7) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    if (days <= 30) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  };

  const completeTodo = async () => {
    if (!selectedTodo) return;

    setIsCompletingTodo(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sie müssen angemeldet sein.");
        return;
      }

      const { error } = await supabase
        .from('todos')
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
          completed_comment: completionComment
        })
        .eq('id', selectedTodo.id);

      if (error) {
        console.error('Error completing todo:', error);
        toast.error("To-Do konnte nicht abgeschlossen werden.");
        return;
      }

      if (selectedTodo.customer_id) {
        const protocolEntry = `Aufgabe abgeschlossen: ${selectedTodo.title}${completionComment ? ` - ${completionComment}` : ''}`;
        await addProtocolEntry(selectedTodo.customer_id, protocolEntry);
      }

      try {
        await supabase.functions.invoke('send-task-notification', {
          body: { todoId: selectedTodo.id, type: 'completed' }
        });
      } catch (emailError) {
        console.error('Error sending task notification:', emailError);
      }

      setSelectedTodo(null);
      setCompletionComment("");
      loadTodos();

      toast.success("Die Aufgabe wurde erfolgreich abgeschlossen.");
    } catch (error) {
      console.error('Error:', error);
      toast.error("Ein unerwarteter Fehler ist aufgetreten.");
    } finally {
      setIsCompletingTodo(false);
    }
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unbekannter Kunde';
  };

  const teamTasks = defaultTasks.filter(t => t.source === 'team');
  const orgTasks = defaultTasks.filter(t => t.source === 'organization');

  if (loading) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">Lade To-Dos...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5" />
                Anstehende To-Dos
              </CardTitle>
              <CardDescription>
                Die nächsten 4 Aufgaben im Überblick
              </CardDescription>
            </div>

            <Dialog open={isCreating} onOpenChange={(open) => {
              setIsCreating(open);
              if (!open) resetDialog();
            }}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Neue Aufgabe</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="mx-4 max-w-md md:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-lg">
                    <CalendarClock className="h-5 w-5" />
                    Neue Aufgabe erstellen
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    Wählen Sie eine vordefinierte Aufgabe oder erstellen Sie eine eigene
                  </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                  {/* Customer Selection */}
                  <div className="space-y-2">
                    <Label>Kunde auswählen *</Label>
                    <Popover open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            "w-full justify-between",
                            newTodo.customerName && "border-primary"
                          )}
                        >
                          {newTodo.customerName || "Kunde auswählen..."}
                          <ChevronRight className="h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Kunde suchen..." />
                          <CommandList>
                            <CommandEmpty>Kein Kunde gefunden.</CommandEmpty>
                            <CommandGroup>
                              {customers.map((customer) => (
                                <CommandItem
                                  key={customer.id}
                                  value={customer.name}
                                  onSelect={() => {
                                    setNewTodo(prev => ({
                                      ...prev,
                                      customerId: customer.id,
                                      customerName: customer.name
                                    }));
                                    setCustomerSearchOpen(false);
                                  }}
                                >
                                  <Check className={cn(
                                    "mr-2 h-4 w-4",
                                    newTodo.customerId === customer.id ? "opacity-100" : "opacity-0"
                                  )} />
                                  {customer.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Separator />

                  {/* Task Selection */}
                  {isLoadingTasks ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : defaultTasks.length === 0 && !showCustomForm ? (
                    <div className="text-center py-6 space-y-3">
                      <Clock className="h-10 w-10 mx-auto text-muted-foreground opacity-50" />
                      <p className="text-sm text-muted-foreground">Keine Team-Aufgaben verfügbar</p>
                      <Button variant="outline" onClick={() => setShowCustomForm(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Eigene Aufgabe erstellen
                      </Button>
                    </div>
                  ) : !selectedTask && !showCustomForm ? (
                    <div className="space-y-4">
                      {/* Team Tasks */}
                      {teamTasks.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">Team-Aufgaben</span>
                          </div>
                          <div className="space-y-2">
                            {teamTasks.map((task) => (
                              <div
                                key={task.id}
                                className="p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                                onClick={() => handleTaskSelect(task)}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-sm">{task.name}</span>
                                      <Badge className={cn("text-xs", getDueDaysColor(task.due_in_days))}>
                                        <Clock className="h-3 w-3 mr-1" />
                                        {getDueDaysLabel(task.due_in_days)}
                                      </Badge>
                                    </div>
                                    {task.description && (
                                      <p className="text-xs text-muted-foreground mt-1 truncate">{task.description}</p>
                                    )}
                                  </div>
                                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Organization Tasks */}
                      {orgTasks.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">Organisations-Aufgaben</span>
                          </div>
                          <div className="space-y-2">
                            {orgTasks.map((task) => (
                              <div
                                key={task.id}
                                className="p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                                onClick={() => handleTaskSelect(task)}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-sm">{task.name}</span>
                                      <Badge className={cn("text-xs", getDueDaysColor(task.due_in_days))}>
                                        <Clock className="h-3 w-3 mr-1" />
                                        {getDueDaysLabel(task.due_in_days)}
                                      </Badge>
                                    </div>
                                    {task.description && (
                                      <p className="text-xs text-muted-foreground mt-1 truncate">{task.description}</p>
                                    )}
                                  </div>
                                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Custom Task Button */}
                      <Separator />
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => setShowCustomForm(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Eigene Aufgabe erstellen
                      </Button>
                    </div>
                  ) : (
                    /* Selected Task or Custom Form */
                    <div className="space-y-4">
                      {selectedTask && (
                        <div className="p-3 border rounded-lg bg-primary/5 border-primary/20">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                {selectedTask.source === 'team' ? (
                                  <Users className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                )}
                                <span className="font-medium">{selectedTask.name}</span>
                              </div>
                              {selectedTask.description && (
                                <p className="text-xs text-muted-foreground mt-1">{selectedTask.description}</p>
                              )}
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedTask(null);
                                setNewTodo(prev => ({
                                  ...prev,
                                  customTitle: "",
                                  customDescription: "",
                                  customDays: 7
                                }));
                              }}
                            >
                              Ändern
                            </Button>
                          </div>
                        </div>
                      )}

                      {showCustomForm && (
                        <>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Eigene Aufgabe</Label>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setShowCustomForm(false);
                                setNewTodo(prev => ({
                                  ...prev,
                                  customTitle: "",
                                  customDescription: "",
                                  customDays: 7
                                }));
                              }}
                            >
                              Zurück
                            </Button>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Titel *</Label>
                            <Input
                              value={newTodo.customTitle}
                              onChange={(e) => setNewTodo(prev => ({ ...prev, customTitle: e.target.value }))}
                              placeholder="z.B. Nachfassen, Anrufen..."
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Beschreibung *</Label>
                            <Textarea
                              value={newTodo.customDescription}
                              onChange={(e) => setNewTodo(prev => ({ ...prev, customDescription: e.target.value }))}
                              placeholder="Details zur Aufgabe..."
                              rows={2}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Fällig in (Tage)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="365"
                              value={newTodo.customDays}
                              onChange={(e) => setNewTodo(prev => ({ ...prev, customDays: parseInt(e.target.value) || 7 }))}
                            />
                          </div>
                        </>
                      )}

                      <Separator />

                      <div className="flex flex-col sm:flex-row justify-end gap-2">
                        <Button variant="outline" onClick={resetDialog} size="sm">
                          Abbrechen
                        </Button>
                        <Button 
                          onClick={createTodo} 
                          size="sm"
                          disabled={!newTodo.customerId || (!selectedTask && !showCustomForm)}
                        >
                          To-Do erstellen
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>

        <CardContent className="min-w-0 max-w-full">
          {todos.length > 0 ? (
            <div className="space-y-3">
              {todos.map((todo) => {
                const daysUntil = getDaysUntilDue(todo.due_date || '');
                const PriorityIcon = getPriorityIcon(daysUntil);

                return (
                  <div
                    key={todo.id}
                    className="p-3 border rounded-lg hover:bg-muted/50 transition-colors min-w-0 cursor-pointer"
                    onClick={() => setSelectedTodo(todo)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <Badge variant={getPriorityColor(daysUntil)} className="text-xs">
                        {daysUntil < 0 ? `${Math.abs(daysUntil)} Tage überfällig` :
                          daysUntil === 0 ? 'Heute' :
                            daysUntil === 1 ? 'Morgen' :
                              `${daysUntil} Tage`}
                      </Badge>
                      <div className="flex-1"></div>
                    </div>

                    <div className="mb-2">
                      <p className="text-xs text-muted-foreground">
                        {todo.customer_id ? getCustomerName(todo.customer_id) : 'Kein Kunde zugeordnet'}
                      </p>
                    </div>

                    <p className="text-sm font-medium text-foreground truncate mb-1">{todo.title}</p>

                    {todo.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {todo.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-sm text-muted-foreground mb-4">Keine offenen Aufgaben</p>
              <Link to="/todo-management">
                <Button variant="outline" size="sm">
                  Alle Aufgaben anzeigen
                </Button>
              </Link>
            </div>
          )}

          {todos.length > 0 && (
            <div className="mt-4 pt-3 border-t">
              <Link to="/todos" className="text-sm text-primary hover:underline">
                Alle To-Dos anzeigen →
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completion Dialog */}
      <Dialog open={!!selectedTodo} onOpenChange={(open) => !open && setSelectedTodo(null)}>
        <DialogContent className="mx-4 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Aufgabe abschließen
            </DialogTitle>
            <DialogDescription>
              {selectedTodo?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">{selectedTodo?.title}</p>
              {selectedTodo?.description && (
                <p className="text-xs text-muted-foreground mt-1">{selectedTodo.description}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Kunde: {selectedTodo?.customer_id ? getCustomerName(selectedTodo.customer_id) : 'Nicht zugeordnet'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Kommentar (optional)</Label>
              <Textarea
                value={completionComment}
                onChange={(e) => setCompletionComment(e.target.value)}
                placeholder="z.B. Ergebnis des Telefonats..."
                rows={3}
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedTodo(null)} size="sm">
                Abbrechen
              </Button>
              <Button onClick={completeTodo} disabled={isCompletingTodo} size="sm">
                {isCompletingTodo ? 'Wird gespeichert...' : 'Als erledigt markieren'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
